import { CatalogCountry, CatalogOccasion, CatalogOverride } from './occasion-catalog'

// What a downloaded catalog must look like to be trusted. Kept apart from
// catalog-source.ts, which owns the storage and network side, so these stay
// pure — they decide what the calendar shows and are worth testing directly.
//
// Deliberately structural and no more. It must NOT reject an occasion whose
// date rule this build does not recognise: the whole point of sending rules
// rather than dates is that a newer catalog can carry a shape an older app has
// never seen, and the resolver already skips those one at a time. Throwing the
// country away instead would take every other occasion in it with them.

export interface ICatalogIndex {
    version: number // monotonic; an older payload is ignored
    schema: number
    countries: CatalogCountry[]
}

export interface ICatalogCountry {
    version: number
    code: string
    definitions: CatalogOccasion[]
    overrides: CatalogOverride[]
}

// The schema this build understands. A feed that moves past it is left alone
// rather than half-read, and the bundled copy carries on.
export const SCHEMA = 1

const isVersion = (v: unknown) => Number.isInteger(v) && (v as number) >= 1

export function validateIndex(payload: unknown): ICatalogIndex | null {
    if (!payload || typeof payload !== 'object') return null
    const { version, schema, countries } = payload as ICatalogIndex
    if (!isVersion(version) || schema !== SCHEMA) return null
    if (!Array.isArray(countries)) return null
    // A country needs the three fields every reader depends on. One malformed
    // entry is dropped; it must not cost the rest of the list.
    const kept = countries.filter(c =>
        c && typeof c.code === 'string' && typeof c.slug === 'string'
        && !!c.names && typeof c.names.en === 'string'
        && Array.isArray(c.regions))
    return kept.length ? { version, schema, countries: kept } : null
}

export function validateCountry(payload: unknown): ICatalogCountry | null {
    if (!payload || typeof payload !== 'object') return null
    const { version, code, definitions, overrides } = payload as ICatalogCountry
    if (!isVersion(version) || typeof code !== 'string') return null
    if (!Array.isArray(definitions) || !Array.isArray(overrides)) return null
    // An occasion needs an id and a name to be shown at all. Whether its date
    // rule can be resolved is the resolver's business, not this one's.
    const kept = definitions.filter(d =>
        d && typeof d.id === 'string' && !!d.names && typeof d.names.en === 'string')
    return {
        version,
        code,
        definitions: kept,
        overrides: overrides.filter(o => o && typeof o.occasionId === 'string'
            && Number.isInteger(o.year)),
    }
}

// The version an app should hold for one country: the country's own number
// when the index carries it, otherwise the index version — which is what
// feeds before per-country versions stamped on every file.
export function remoteCountryVersion(index: ICatalogIndex | null, code: string): number | undefined {
    if (!index) return undefined
    const row = index.countries.find(c => c.code === code)
    if (!row) return undefined
    return isVersion(row.version) ? row.version : index.version
}

// A downloaded copy only replaces what is stored when it is genuinely newer.
// Equal or older is ignored, so a stale mirror cannot roll a correction back.
export function shouldReplace(localVersion: number, remoteVersion: number | undefined): boolean {
    return remoteVersion !== undefined && remoteVersion > localVersion
}
